import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, Loader2, LifeBuoy } from "lucide-react";
import { PageHero } from "@/pages/HowItWorks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

export default function Support() {
  const [info, setInfo] = useState({ support_email: "", support_whatsapp: "" });
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    api.get("/support/contact").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const waDigits = (info.support_whatsapp || "").replace(/[^\d]/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.subject.trim() || !f.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/contact", f);
      toast.success("Thanks! Your message has been sent to our support team.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="support-page">
      <PageHero eyebrow="Contact / Support" title="We're Here to Help" subtitle="Questions about memberships, partners or payments? Reach the FitLynk team directly or send us a message." />
      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto grid lg:grid-cols-5 gap-8">
          {/* Contact channels */}
          <div className="lg:col-span-2 space-y-4" data-testid="support-channels">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#E53E3E]/10 text-[#E53E3E]"><LifeBuoy className="w-5 h-5" /></span>
              <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight">Get in touch</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">Our support team typically replies within 24 hours.</p>
            </div>

            {info.support_email && (
              <a href={`mailto:${info.support_email}`} className="flex items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 hover:border-[#E53E3E] transition-colors group" data-testid="support-email-link">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-zinc-800 text-[#E53E3E] group-hover:bg-[#E53E3E] group-hover:text-white transition-colors"><Mail className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Email us</p>
                  <p className="text-sm text-white truncate">{info.support_email}</p>
                </div>
              </a>
            )}

            {info.support_whatsapp && (
              <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 hover:border-[#25D366] transition-colors group" data-testid="support-whatsapp-link">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-zinc-800 text-[#25D366] group-hover:bg-[#25D366] group-hover:text-white transition-colors"><MessageCircle className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">WhatsApp</p>
                  <p className="text-sm text-white truncate">{info.support_whatsapp}</p>
                </div>
              </a>
            )}
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 sm:p-8 space-y-5" data-testid="support-form">
              <h3 className="font-display text-xl font-bold uppercase tracking-tight">Send us a message</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name</Label>
                  <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="h-11 bg-zinc-900 border-zinc-800" data-testid="support-form-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Email</Label>
                  <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="h-11 bg-zinc-900 border-zinc-800" data-testid="support-form-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Subject</Label>
                <Input value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="How can we help?" className="h-11 bg-zinc-900 border-zinc-800" data-testid="support-form-subject" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Message</Label>
                <Textarea value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a bit more..." rows={6} className="bg-zinc-900 border-zinc-800 resize-none" data-testid="support-form-message" />
              </div>
              <Button type="submit" disabled={sending} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11 px-6" data-testid="support-form-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
